// src/hooks/useOutputEntitlements.js
//
// Which output types this workspace's plan buys, read once per tenant from
// GET /api/v1/entitlements.
//
// ⭐ This hook answers ONE question: *may this workspace run this output
// type?* The other question — *are there credits to run at all?* — belongs to
// the backend and reaches the FE as a coded refusal on the run. It is NOT
// mixed in here: useEntitlements.js (the wallet) is a different hook on a
// different endpoint.
//
// ⛔⛔ `variants === null` and `variants === []` ARE DIFFERENT ANSWERS:
//     null → NOT KNOWN     → offer everything (fail open)
//     []   → NOTHING GRANTED → grey everything + say why (`reason`)
// Under the old allowed_formats grammar `[]` was unreachable; under this one
// it is the normal refusal shape. Conflating the two either greys a healthy
// customer's whole catalogue or offers a plan-less one everything — silently,
// with no way for them to tell.
//
// ⛔ A 503 keeps the LAST KNOWN state (see outputEntitlementsStore.js) and
// must be detected as `status === 503 && code === 'LOOKUP_FAILED'` — the
// endpoint's `is_retryable` arrives FALSE although the condition is
// retryable (known BE defect, recorded in their handoff §3.1).
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchEntitlements } from '../services/api/entitlementsApi.js';
import {
  readCache, writeCache, markStale, subscribe, getEpoch,
} from './outputEntitlementsStore.js';

/**
 * The `reason` values that mean "nothing is granted, and here is why".
 * ⚠️ `FEATURE_NOT_IN_PLAN` HERE means the plan does not buy the pipeline at
 * all — the reason is computed against the entry feature `cg:pipeline` alone.
 * It never means "that one output type is missing"; that is the 402 on the run.
 */
export const REFUSAL_REASONS = new Set([
  'NO_PLAN',
  'FEATURE_NOT_IN_PLAN',
  'APP_NOT_ASSIGNED',
]);

/** The permissive baseline: "not known" — offer everything. */
const UNKNOWN = {
  variants: null,
  reason: null,
  planId: null,
  grantKey: null,
  stale: false,
};

export function useOutputEntitlements() {
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?.tenant_id;

  const [state, setState] = useState(() => {
    const cached = readCache(tenantId);
    return cached
      ? { ...cached, loading: false, error: null }
      : { ...UNKNOWN, loading: !!tenantId, error: null };
  });

  const [tick, setTick] = useState(getEpoch());
  useEffect(() => subscribe(setTick), []);

  useEffect(() => {
    // ⛔ No tenant → NO CALL. Not hygiene: this route falls back to the
    // server's default tenant, which is EXEMPT and answers with the FULL
    // catalogue. A tenantless call silently unlocks everything.
    if (!tenantId) {
      setState({ ...UNKNOWN, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    const cached = readCache(tenantId);
    setState(cached
      ? { ...cached, loading: true, error: null }
      : { ...UNKNOWN, loading: true, error: null });

    fetchEntitlements({ tenant: tenantId })
      .then(payload => {
        if (cancelled) return;
        const entry = {
          // Anything that is not an array — absent, null, malformed, or an
          // old backend — is "not known", NOT "nothing granted".
          variants: Array.isArray(payload?.variants) ? payload.variants : null,
          reason: payload?.reason ?? null,
          planId: payload?.plan_id ?? null,
          grantKey: payload?.grant_key ?? null,
        };
        writeCache(tenantId, entry);
        setState({ ...entry, stale: false, loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        // ⛔ Branch on the CODE + status, never on `is_retryable`.
        if (err?.status === 503 && err?.code === 'LOOKUP_FAILED') {
          const kept = markStale(tenantId);
          if (kept) {
            setState({ ...kept, loading: false, error: err });
            return;
          }
        }
        // Everything else — network, 403, 404 (a backend that predates the
        // route), a 503 with nothing cached — falls OPEN.
        setState({ ...UNKNOWN, loading: false, error: err });
      });

    return () => { cancelled = true; };
  }, [tenantId, tick]);

  return state;
}

export default useOutputEntitlements;
